import React, { useEffect, useState, useContext, useMemo } from 'react';
import Grid from '../components/Grid';
import { SettingsContext } from './Main';
import { Project } from '../models/Project';
import { Module } from '../models/Module';
import { Slide } from '../models/Slide';
import { ColumnDef, Row } from "@tanstack/react-table";
import { debounce } from 'lodash';
import { PlayCircle, FilePenLine, Archive, FileInput, PackageOpen, CircleX, LoaderCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/Accordion";
import SlideEditor from '../editors/SlideEditor';
import ModuleEditor from '../editors/ModuleEditor';
import ProjectEditor from '../editors/ProjectEditor';
import { useToast } from '../components/ToastContext';
import { AccordionHeader } from '@radix-ui/react-accordion';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import TooltipWrapper from '../components/ui/TooltipWrapper';
import SelectDropdown from '../components/SelectDropdown';
import { Tooltip } from 'react-tooltip';

function SlideManagement() {
    const [projects, setProjects] = useState<Project[]>([]);
    const [activeProject, setActiveProject] = useState<Project>();
    const [includeArchivedProjects, setIncludeArchivedProjects] = useState(false);
    const [includeArchivedModules, setIncludeArchivedModules] = useState(false);

    const { setIsToastOpen, setToastMessage, setToastTitle } = useToast();
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoaded, setIsLoaded] = useState(false);

    const buttonCls = 'text-white bg-primary-blue hover:bg-blue-800 focus:outline-none font-medium rounded-lg text-sm mr-4 px-2.5 py-2 text-center';
    const searchInputCls = "appearance-none block flex-1 text-gray-700 border border-gray-200 rounded py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500";

    const { SERVER_ROOT_URL } = useContext(SettingsContext);

    const onActiveProjectIDChange = (activeProjectID: string) => {
        var selectedProject = projects.find(item => item.projectID === activeProjectID);

        if (selectedProject) {
            loadProjectWithModules(selectedProject);
        }
    }

    const onSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
        debouncedOnChange(e.currentTarget.value);
    }

    const debouncedOnChange = useMemo(() => {
        return debounce((value: string) => {
            setSearchQuery(value);
        }, 1000);
    }, []);

    const loadProjects = async () => {
        try {
            setIsLoaded(false);

            var projectUrl = SERVER_ROOT_URL + "/projects";
            if (includeArchivedProjects) {
                projectUrl += "?includeArchive=true";
            }
            let projectData: Project[] = await fetch(projectUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            }).then((response) => {
                return response.json();
            });

            setProjects(projectData);

            const storedLastActiveProject = localStorage.getItem('LastActiveProject');
            const activeProject = projectData.find(item => item.projectID === storedLastActiveProject) || projectData[0];

            if (activeProject) {
                loadProjectWithModules(activeProject);
            }

            setIsLoaded(true);
        } catch {
            setProjects([]);
        }
    }

    const loadProjectWithModules = async (project: Project) => {
        project.modules = await loadModules(project);
        setActiveProject(project);
        localStorage.setItem("LastActiveProject", project.projectID);
    }

    useEffect(() => {
        (async function () {
            await loadProjects();
        })();

        /*
        * TODO: Disable this lint by wrapping loadProjects in a useCallback, 
        * then add a ref to indicate the initial load and prevent infinite loops
        */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, includeArchivedProjects, includeArchivedModules]);

    const loadModules = async (project: Project): Promise<Module[]> => {
        try {
            var moduleUrl = `${SERVER_ROOT_URL}/${project.projectID}/modules`;
            if (includeArchivedModules) {
                moduleUrl += "?includeArchive=true";
            }
            const moduleData = await fetch(moduleUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            }).then((response) => {
                return response.json();
            });

            const updatedModules = await Promise.all(
                (moduleData as Module[]).map(async (mod) => {
                    mod.slides = await loadslides(mod); // Load modules for each project
                    return mod;
                })
            );

            return updatedModules;
        } catch {
            return [];
        }
    }

    const loadslides = async (module: Module) => {
        try {
            const queryUrl = searchQuery ? `?searchQuery=${searchQuery}` : "";
            let slideData = await fetch(`${SERVER_ROOT_URL}/${module.projectID}/${module.moduleID}/slides/${queryUrl}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            }).then((response) => {
                return response.json();
            });

            return slideData;
        } catch {
            return [];
        }
    }

    const createProject = async (project: Project) => {
        try {
            const response = await fetch(SERVER_ROOT_URL + "/project", {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                method: 'POST',
                body: JSON.stringify(project)
            });

            if (response.ok) {
                setToastTitle("Success");
                setToastMessage("Project created.");

                loadProjects();
            } else {
                setToastTitle("Error");
                setToastMessage((await response.json()).message);
            }
        } catch {
            setToastTitle("Error");
            setToastMessage("Project creation unsuccessful. Please try again.")
        }
        setIsToastOpen(true);
    }

    const updateProject = async (project: Project) => {
        try {
            const response = await fetch(SERVER_ROOT_URL + "/project", {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                method: 'PUT',
                body: JSON.stringify(project)
            });

            if (response.ok) {
                setToastTitle("Success");
                setToastMessage("Project created.");

                loadProjects();
            } else {
                setToastTitle("Error");
                setToastMessage((await response.json()).message);
            }
        } catch {
            setToastTitle("Error");
            setToastMessage("Project creation unsuccessful. Please try again.")
        }
        setIsToastOpen(true);
    }

    const createModule = async (module: Module) => {
        try {
            const response = await fetch(SERVER_ROOT_URL + "/module", {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                method: 'POST',
                body: JSON.stringify(module)
            });

            if (response.ok) {
                setToastTitle("Success");
                setToastMessage("Module created.");

                var parentProject = projects.find(item => item.projectID === module.projectID);
                if (parentProject) {
                    loadProjectWithModules(parentProject);
                }
            } else {
                setToastTitle("Error");
                setToastMessage((await response.json()).message);
            }
        } catch {
            setToastTitle("Error");
            setToastMessage("Module creation unsuccessful. Please try again.")
        }
        setIsToastOpen(true);
    }

    const updateModule = async (module: Module) => {
        try {
            const response = await fetch(SERVER_ROOT_URL + "/module", {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                method: 'PUT',
                body: JSON.stringify(module)
            });

            if (response.ok) {
                setToastTitle("Success");
                setToastMessage("Module updated.");

                var parentProject = projects.find(item => item.projectID === module.projectID);
                if (parentProject) {
                    loadProjectWithModules(parentProject);
                }
            } else {
                setToastTitle("Error");
                setToastMessage((await response.json()).message);
            }
        } catch {
            setToastTitle("Error");
            setToastMessage("Module update unsuccessful. Please try again.")
        }
        setIsToastOpen(true);
    }

    const createSlide = async (slide: Slide, files: FileList, creationType: string) => {
        try {
            const formData = new FormData();

            for (let i = 0; i < files.length; i++) {
                formData.append('Content', files[i]);
            }

            formData.append('ProjectID', slide.projectID);
            formData.append('ModuleID', slide.moduleID);
            formData.append('SlideID', slide.slideID);
            formData.append("CreationType", creationType);
            const response = await fetch(SERVER_ROOT_URL + "/slide", {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                setToastTitle("Success");
                setToastMessage("Slide created.");

                var parentProject = projects.find(item => item.projectID === slide.projectID);
                if (parentProject) {
                    loadProjectWithModules(parentProject);
                }
            } else {
                setToastTitle("Error");
                setToastMessage((await response.json()).message);
            }
        } catch {
            setToastTitle("Error");
            setToastMessage("Slide creation unsuccessful. Please try again.")
        }
        setIsToastOpen(true);
    }

    const onArchiveModuleClick = async (mod: Module) => {
        if (mod.archiveDate) {
            mod.archiveDate = undefined;
        } else {
            mod.archiveDate = new Date();
        }
        await updateModule(mod);
    }

    const onArchiveProjectClick = async (proj: Project | undefined) => {
        if (proj) {
            if (proj.archiveDate) {
                proj.archiveDate = undefined;
            } else {
                proj.archiveDate = new Date();
            }
            await updateProject(proj);
        } else {
            setToastTitle("Error");
            setToastMessage("No active project selected.")
            setIsToastOpen(true);
        }
    }

    const onLaunchClick = (row: Row<Slide>) => {
        var launchUrl = `slides/${row.original.projectID}/${row.original.moduleID}/${row.original.slideID}`;
        window.open(launchUrl, "_blank");
    }

    const onEditClick = (row: Row<Slide>) => {
        var editUrl = `slides/${row.original.projectID}/${row.original.moduleID}/${row.original.slideID}/?inEditMode=true`;
        window.open(editUrl, "_blank");
    }

    const onDeleteClick = async (row: Row<Slide>) => {
        try {
            const response = await fetch(`${SERVER_ROOT_URL}/${row.original.projectID}/${row.original.moduleID}/${row.original.slideID}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                method: 'DELETE'
            });

            if (response.ok) {
                setToastTitle("Success");
                setToastMessage("Slide archived.");

                loadProjects();
            } else {
                setToastTitle("Error");
                setToastMessage((await response.json()).message);
            }

        } catch {
            setToastTitle("Error");
            setToastMessage("Slide deletion unsuccessful. Please try again.")
        }
        setIsToastOpen(true);
    }

    const onExportClick = (row: Row<Slide>) => {
        var exportUrl = `${SERVER_ROOT_URL}/${row.original.projectID}/${row.original.moduleID}/${row.original.slideID}/export`;
        window.open(exportUrl, "_blank");
    }

    const SlideColumns: ColumnDef<Slide>[] = [
        {
            accessorKey: "slideID",
            header: "Slide"
        },
        {
            accessorKey: "modificationTime",
            header: "Last Modified",
            accessorFn: (originalRow: Slide) => {
                return new Date(originalRow.modificationTime).toString();
            }
        },
        {
            id: "actions",
            enableHiding: false,
            size: 155,
            cell: ({ row }) => {
                return (
                    <div className="flex flex-wrap">
                        <TooltipWrapper 
                            trigger={<PlayCircle className='me-2 cursor-pointer hover:text-slate-400' onClick={() => { onLaunchClick(row) }} />} 
                            message="Open in view mode" />
                        <TooltipWrapper 
                            trigger={<FilePenLine className='me-2 cursor-pointer hover:text-slate-400' onClick={() => { onEditClick(row) }} />} 
                            message="Open in edit mode" />
                        <ConfirmDialog 
                            title='Are you absolutely sure?' 
                            description='This action will delete the slide'
                            cancelText='Cancel'
                            confirmText='Yes, delete slide'
                            trigger={
                                <div>
                                    <CircleX className='me-2 cursor-pointer hover:text-slate-400'
                                        data-tooltip-id={"delete-slide-" + row.original.projectID + row.original.moduleID + "-" + row.original.slideID}
                                        data-tooltip-content="Delete slide"
                                        data-tooltip-place='top' />
                                    <Tooltip id={"delete-slide-" + row.original.projectID + row.original.moduleID + "-" + row.original.slideID} />
                                </div>} 
                            onConfirm={async () => { await onDeleteClick(row); }}  />

                        <TooltipWrapper 
                            trigger={<FileInput className='cursor-pointer hover:text-slate-400' onClick={() => { onExportClick(row) }} />} 
                            message="Export" />
                    </div>
                );
            }
        }
    ]

    return (
        <div className='container mx-auto my-3 rounded shadow'>
            <div className='w-full flex my-3 justify-between'>
                <div className='justify-start'>
                    <SlideEditor projects={projects} creationType="import" createSlide={createSlide} 
                        trigger={<button type="button" className={buttonCls}>Import CP6 Module</button>}/>
                    <SlideEditor projects={projects} creationType="new" createSlide={createSlide} 
                        trigger={<button type="button" className={buttonCls}>Add new slide</button>}/>
                    <ModuleEditor projects={projects} createModule={createModule} 
                        trigger={<button type="button" className={buttonCls}>Add module</button>}/>
                    <ProjectEditor createProject={createProject}
                        trigger={<button type="button" className={buttonCls}>Add project</button>} 
                    />
                </div>
                <div className='justify-end'>
                    <button className={buttonCls} 
                        onClick={() => setIncludeArchivedProjects(!includeArchivedProjects)}>{ includeArchivedProjects ? "Hide" : "Show"} archived projects</button>
                    <button className={buttonCls.replace("mr-4", "")} 
                        onClick={() => setIncludeArchivedModules(!includeArchivedModules)}>{ includeArchivedModules ? "Hide" : "Show"} archived modules</button>
                </div>
            </div>
            {
                activeProject !== undefined
                    ? 
                        <>
                            <div className='w-full flex my-3'>
                                <SelectDropdown
                                    value={activeProject?.projectID ?? ""}
                                    options={new Map(projects.map(proj => [proj.projectID, proj.projectID + " - " + proj.title]))}
                                    onSelectChange={onActiveProjectIDChange} />
                            </div>
                            <div className='w-full flex my-3'>
                                <input className={searchInputCls} placeholder='Search..' onChange={onSearchChange} />
                            </div>
                            <div className='w-full flex my-3'>
                                <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
                                    <AccordionItem value="item-1">
                                        <AccordionHeader>
                                            <div className='relative text-left w-full'>
                                                <span className="absolute top-0 end-10 h-full px-4 cursor-pointer inline-flex items-center rounded-full hover:text-slate-400">
                                                    <ProjectEditor createProject={createProject}
                                                        trigger={
                                                            <div>
                                                                <FilePenLine className='cursor-pointer hover:text-slate-400'
                                                                    data-tooltip-id="edit-project" 
                                                                    data-tooltip-content="Edit project"
                                                                    data-tooltip-place='top' />
                                                                <Tooltip id="edit-project" />
                                                            </div>} 
                                                        existingProject={activeProject}
                                                        updateProject={updateProject}/>
                                                </span>
                                                <ConfirmDialog 
                                                    title='Are you absolutely sure?' 
                                                    description={activeProject?.archiveDate ? 'This action will unarchive all associated modules and slides' : 'This action will archive all associated modules and slides'}
                                                    cancelText='Cancel'
                                                    confirmText={activeProject?.archiveDate ? 'Yes, unarchive project' : 'Yes, archive project'}
                                                    trigger={
                                                        <div>
                                                            <span className="absolute top-0 end-2 h-full px-4 cursor-pointer inline-flex items-center rounded-full hover:text-slate-400">
                                                                {activeProject?.archiveDate 
                                                                    ? <PackageOpen className='cursor-pointer hover:text-slate-400' 
                                                                        data-tooltip-id="unarchive-project"
                                                                        data-tooltip-content="Unarchive project"
                                                                        data-tooltip-place='top'/> 
                                                                    : <Archive className='cursor-pointer hover:text-slate-400' 
                                                                        data-tooltip-id="archive-project" 
                                                                        data-tooltip-content="Archive project"
                                                                        data-tooltip-place='top'/>}
                                                            </span>
                                                            <Tooltip id="unarchive-project" />
                                                            <Tooltip id="archive-project" />
                                                        </div>}
                                                    onConfirm={async () => { await onArchiveProjectClick(activeProject);}}/>
                                                <AccordionTrigger>{activeProject?.projectID + " - " + activeProject?.title}</AccordionTrigger>
                                            </div>
                                        </AccordionHeader>
                                        <AccordionContent asChild>
                                            <div className="ml-5">
                                                {
                                                    activeProject?.modules?.map((mod, idx) => (
                                                        <Accordion key={idx} type="single" collapsible defaultValue={mod?.slides?.length ? "item-1" : ""} className="w-full m-">
                                                            <AccordionItem value="item-1">
                                                                <AccordionHeader>
                                                                    <div className='relative text-left w-full'>
                                                                        <span className="absolute top-0 end-10 h-full px-4 cursor-pointer inline-flex items-center rounded-full hover:text-slate-400">
                                                                            <ModuleEditor projects={projects} createModule={createModule} existingModule={mod} updateModule={updateModule}
                                                                                trigger={<div>
                                                                                    <FilePenLine className='cursor-pointer hover:text-slate-400'
                                                                                        data-tooltip-id={"edit-module" + mod.moduleID}
                                                                                        data-tooltip-content="Edit project"
                                                                                        data-tooltip-place='top' />
                                                                                    <Tooltip id={"edit-module" + mod.moduleID} />
                                                                                </div>}/>
                                                                        </span>
                                                                        <ConfirmDialog 
                                                                            title='Are you absolutely sure?' 
                                                                            description={mod.archiveDate ? 'This action will unarchive all associated slides' : 'This action will archive all associated slides'}
                                                                            cancelText='Cancel'
                                                                            confirmText={mod.archiveDate ? 'Yes, unarchive module' : 'Yes, archive module'}
                                                                            trigger={<div>
                                                                                <span className="absolute top-0 end-2 h-full px-4 cursor-pointer inline-flex items-center rounded-full hover:text-slate-400">
                                                                                    {mod.archiveDate 
                                                                                        ? <PackageOpen className='cursor-pointer hover:text-slate-400' 
                                                                                            data-tooltip-id={"unarchive-module" + mod.moduleID}
                                                                                            data-tooltip-content="Unarchive project"
                                                                                            data-tooltip-place='top'/> 
                                                                                        : <Archive className='cursor-pointer hover:text-slate-400' 
                                                                                            data-tooltip-id={"archive-module" + mod.moduleID}
                                                                                            data-tooltip-content="Archive project"
                                                                                            data-tooltip-place='top'/>}
                                                                                </span>
                                                                                <Tooltip id={"unarchive-module" + mod.moduleID} />
                                                                                <Tooltip id={"archive-module" + mod.moduleID} />
                                                                            </div>}
                                                                            onConfirm={async () => { await onArchiveModuleClick(mod) }} />
                                                                        <AccordionTrigger>{mod.moduleID} - {mod.title}</AccordionTrigger>
                                                                    </div>
                                                                </AccordionHeader>

                                                                <AccordionContent>
                                                                    <div className="ml-5">
                                                                        <Grid columns={SlideColumns} data={mod.slides ?? []} />
                                                                    </div>
                                                                </AccordionContent>
                                                            </AccordionItem>
                                                        </Accordion>
                                                    ))
                                                }
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </div>
                        </>
                    : 
                        isLoaded && projects.length === 0
                            ?
                                <div className='my-20 text-center flex justify-center'>
                                    <span className='pl-2'>No projects available</span>
                                </div>
                            :
                                <div className='my-20 text-center flex justify-center'>
                                    <LoaderCircle className='animate-spin'/><span className='pl-2'> Loading projects...</span>
                                </div>
            }

        </div>
    );
}

export default SlideManagement;